
import React from "react";
import { useFolderViewModel } from "./hooks/useFolderViewModel";
import { FolderHeaderSection } from "./FolderHeaderSection";
import { FolderDisplaySection } from "./FolderDisplaySection";
import { FolderLoadingState } from "./FolderLoadingState";
import { FolderErrorState } from "./FolderErrorState";
import type { Database } from "@/integrations/supabase/types";

type DatabaseFolder = Database['public']['Tables']['folders']['Row'];

interface FolderViewProps {
  projectId: string;
  folderId: string;
  onNavigateToFolder: (path?: string) => void;
  onStartAssessment?: (projectId: string, folderId: string) => void;
  onNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  onCreateNewAssessment: (projectId: string, folderId: string) => void;
  fetchProjectIdForSharedFolder?: (folderId: string) => Promise<string | null>;
  isSharedFolder?: boolean;
  onFolderUpdated?: () => void;
}

const FolderView: React.FC<FolderViewProps> = ({
  projectId,
  folderId,
  onNavigateToFolder,
  onStartAssessment,
  onNavigateToAssessment,
  onCreateNewAssessment,
  fetchProjectIdForSharedFolder,
  isSharedFolder = false,
  onFolderUpdated
}) => {
  console.log("FolderView - Received props:", { projectId, folderId, isSharedFolder });

  const {
    folderData,
    isLoading,
    error,
    refetch,
    subfolders,
    assessments,
    isShared,
    handleOpenFolder,
    handleStartAssessment: handleStart,
    handleNavigateToAssessment: handleNavigate,
    handleCreateFolder,
    handleDeleteFolder,
    handleDeleteAssessment,
    refreshData
  } = useFolderViewModel({
    projectId,
    folderId,
    onNavigateToFolder,
    onStartAssessment,
    onNavigateToAssessment,
    isSharedFolder,
    onFolderUpdated
  });

  if (isLoading) {
    return <FolderLoadingState />;
  }

  if (error) {
    return <FolderErrorState error={error} onRetry={refetch} />;
  }

  if (!folderData) {
    return <FolderErrorState error="Mappen kunde inte hittas" onRetry={refetch} />;
  }

  // Convert folderData to match Database type
  const databaseFolderData: DatabaseFolder = {
    id: folderData.id,
    name: folderData.name,
    description: folderData.description || "",
    parent_folder_id: folderData.parent_folder_id || null,
    project_id: folderData.project_id,
    created_at: folderData.created_at || new Date().toISOString(),
    created_by: folderData.created_by || "",
    updated_at: folderData.updated_at || new Date().toISOString(),
    revised_by: folderData.revised_by || ""
  };

  // Convert subfolders to match Database type
  const databaseSubfolders: DatabaseFolder[] = subfolders.map(folder => ({
    id: folder.id,
    name: folder.name,
    description: folder.description || "",
    parent_folder_id: folder.parent_folder_id || null,
    project_id: folder.project_id,
    created_at: folder.created_at || new Date().toISOString(),
    created_by: folder.created_by || "",
    updated_at: folder.updated_at || new Date().toISOString(),
    revised_by: folder.revised_by || ""
  }));

  // Use the actual project ID from folder data or fallback to prop
  const effectiveProjectId = folderData.project_id || projectId;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <FolderHeaderSection
        folderData={databaseFolderData}
        projectId={effectiveProjectId}
        isShared={isShared}
        subfolderCount={databaseSubfolders.length}
        assessmentCount={assessments.length}
      />

      <FolderDisplaySection
        folderData={databaseFolderData}
        subfolders={databaseSubfolders}
        assessments={assessments}
        projectId={effectiveProjectId}
        folderId={folderId}
        isShared={isShared}
        onOpenFolder={handleOpenFolder}
        onStartAssessment={handleStart}
        onNavigateToAssessment={handleNavigate}
        onCreateFolder={handleCreateFolder}
        onDeleteFolder={handleDeleteFolder}
        onDeleteAssessment={handleDeleteAssessment}
        onFolderUpdated={onFolderUpdated}
        onRefresh={refreshData}
      />
    </div>
  );
};

export default FolderView;
